import { useSystemAssets } from '@bz/systemAssets';
import { JSX } from 'solid-js';

import { SupportTips } from './SupportTips';
import { BetslipStatus } from '../../types';
import '../css.css';

export type OverlayProps = {
  type: 'general' | 'failure' | 'single' | 'combo';
  localErrCode?: string;
  status: BetslipStatus;
  message?: string | JSX.Element;
  icon?: JSX.Element;
  errorCode?: string | number;
  buttons?: JSX.Element;
  onClose?: () => void;
  onConfirm?: () => void;
  class?: string;
  hasCloseIcon?: boolean;
  isOpen: boolean;
  countdownSeconds?: number;
  width?: string;
  height?: string;
  ref?: HTMLDivElement | ((el: HTMLDivElement) => void);
};

type Content = {
  message?: string | JSX.Element;
  icon: JSX.Element;
};

// 通用组件
export function Overlay(props: OverlayProps) {
  const [assets] = useSystemAssets();
  // 生成默认消息和图标
  const getContent = (): Content => {
    switch (props.status) {
      case BetslipStatus.LOADING:
        return {
          message: `${t('Please wait.')} ${t('We are processing your bets.')} ${props.countdownSeconds}s...`,
          icon: <img class="loading-animation size-[6.25rem] m-auto" src={assets.loadingGeneral} />,
        };
      case BetslipStatus.SUCCESS:
        return {
          message: `${t('Your bets have been successfully placed!')}`,
          icon: <img class="size-[6.25rem] mb-4 m-auto" src={assets.successGeneral} />,
        };
      case BetslipStatus.EMPTY:
        return {
          message: `${t('Bets is Empty')} ${t('Place Your Bet')}`,
          icon: <img class="size-[6.25rem] mb-4 m-auto" src={assets.emptyGeneral} />,
        };
      case BetslipStatus.ERROR:
        return {
          message: t('Bet Not Placed'),
          icon: <img class="size-[6.25rem] mb-4 m-auto" src={assets.devprogressCombo} />,
        };
      case BetslipStatus.NETWORK_ERROR:
        return {
          message: `${t('Network error,')} ${t('please try again later')}`,
          icon: <img class="size-[6.25rem] mb-4 m-auto" src={assets.networkerrorFailure} />,
        };
      case BetslipStatus.SERVER_ERROR:
        return {
          message: `${t('Network error,')} ${t('please try again later')}`,
          icon: <img class="size-[6.25rem] mb-4 m-auto" src={assets.devprogressCombo} />,
        };
      case BetslipStatus.MBS_REPORT_AILED:
        return {
          message: `${t('Network error,')} ${t('please try again later')}`,
          icon: <img class="size-[6.25rem] mb-4 m-auto" src={assets.devprogressCombo} />,
        };
      case BetslipStatus.TIMEOUT:
        return {
          message: `${t('Timeout occurred,')} ${t('please try again later.')}`,
          icon: <img class="size-[6.25rem] mb-4 m-auto" src={assets.timeoutFailure} />,
        };
      case BetslipStatus.BET_AMOUNT_TOO_SMALL:
        return {
          message: `${t('Timeout occurred,')} ${t('please try again later.')}`,
          icon: <img class="size-[6.25rem] mb-4 m-auto" src={assets.betlimitexceededCombo} />,
        };
      case BetslipStatus.BET_AMOUNT_TOO_LARGE:
        return {
          message: 'Timeout occurred, \n please try again later.',
          icon: <img class="size-[6.25rem] mb-4 m-auto" src={assets.betlimitexceededCombo} />,
        };
      case BetslipStatus.SINGLE_ODDS_CHANGE:
        return {
          message: `${t('Your bets have been')} ${t('partially placed!')} ${t('Change Odds Settings')}`,
          icon: <img class="size-[6.25rem] mb-4 m-auto" src={assets.partialsuccessSingle} />,
        };
      case BetslipStatus.SINGLE_PARTIALLY_PLACED:
        return {
          message: `${t('Your bets have been')} ${t('partially placed!')}`,
          icon: <img class="size-[6.25rem] mb-4 m-auto" src={assets.partialsuccessSingle} />,
        };
      case BetslipStatus.SINGLE_ODDS_PARTIALLY_PLACED:
        return {
          message: `${t('Your bets have been')} ${t('partially placed!')}`,
          icon: <img class="size-[6.25rem] mb-4 m-auto" src={assets.partialsuccessSingle} />,
        };
      case BetslipStatus.SINGLE_ODDS_ALL_FAILED:
        return {
          message: `${t('Your bets have been')} ${t('partially placed!')}`,
          icon: <img class="size-[6.25rem] mb-4 m-auto" src={assets.devprogressCombo} />,
        };
      case BetslipStatus.ALL_BET_FAILED:
        return {
          message: `${t('Your bets have been')} ${t('partially placed!')}`,
          icon: <img class="size-[6.25rem] mb-4 m-auto" src={assets.marketisnotavailableCombo} />,
        };
      case BetslipStatus.COMBO_ODDS_CHANGE:
        return {
          message: `${t('Your bets was declined')} ${t('due to the odds change.')} ${t('Change Odds Settings')}`,
          icon: <img class="size-[6.25rem] mb-4 m-auto" src={assets.devprogressCombo} />,
        };
      case BetslipStatus.COMBO_BALANCE_INSUFFICIENT:
        return {
          message: `${t('7.5$ Bet limit exceeded')}`,
          icon: <img class="size-[6.25rem] mb-4 m-auto" src={assets.betlimitexceededCombo} />,
        };
      case BetslipStatus.COMBO_MARKET_UNAVAILABLE:
        return {
          message: `${t('Market unavailable anymore')}`,
          icon: <img class="size-[6.25rem] mb-4 m-auto" src={assets.marketisnotavailableCombo} />,
        };
      default:
        return {
          message: props.message || t('Default message'),
          icon: props.icon,
        };
    }
  };

  return (
    <div
      ref={props.ref}
      class={cx(
        'bg-layer5-card bg-dark-gray absolute bottom-0 left-0 right-0 flex items-center flex-col z-50 transition-transform duration-300 ease-out',
        props.isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none',
        props.class
      )}
      style={{
        width: props.width ?? '100%',
        height: props.height ?? '100%',
      }}
    >
      <IconCloseMark
        style={{ display: props.hasCloseIcon ? 'block' : 'none' }}
        class="absolute right-[0.75rem] top-[0.75rem] w-[1.5rem] h-[1.5rem] cursor-pointer text-neutral-main1_alt hover:text-brand-default"
        onClick={() => {
          if (props.onClose) {
            props.onClose();
          }
        }}
      />

      {/* 图标和消息 */}
      <div class="flex-1 flex flex-col justify-center items-center">
        <span>{getContent().icon}</span>
        <p class="text-center text-white mt-2 whitespace-pre-wrap break-words text-[#FFF] leading-[1.5rem]">
          {props.message || getDefaultMessageByStatus(props)}
        </p>
        <Show when={props.localErrCode === '10000'}>
          <SupportTips />
        </Show>
      </div>

      {/* 按钮 */}
      <div class="flex justify-center w-full">
        <div class="w-full px-4 pt-0 flex flex-col">
          {props.buttons || getDefaultButtonByStatus(props)}
          <Show
            when={props.status === BetslipStatus.COMBO_ODDS_CHANGE || props.status === BetslipStatus.SINGLE_ODDS_CHANGE}
          >
            <div class="flex py-[0.25rem] px-0 mt-2 justify-center items-start gap-[0.25rem] self-stretch rounded-[0.125rem] bg-[rgba(253,162,78,0.10)]">
              <IconBetslipWarning class="text-[#FFC100] size-[0.9rem]" />
              <span class="text-[#FFC100] text-xs font-semibold leading-[0.9rem] gap-2">
                {t('By clicking Place Bet, you accept odds change')}
              </span>
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
}

const getDefaultMessageByStatus = (props: any): any => {
  switch (props.status) {
    case BetslipStatus.LOADING:
      return (
        <div class="flex flex-col items-center">
          <span class="text-neutral-main1 text-sm font-semibold">{t('Please wait')}</span>
          <span class="text-neutral-main2">{t('We are processing your bets')}</span>
          <div class="text-[#00DC93] mt-2">{props.countdownSeconds}s...</div>
        </div>
      );
    case BetslipStatus.SUCCESS:
      return (
        <div class="flex flex-col items-center">
          <span class="text-neutral-main1 text-sm font-semibold">{t('Successful Bet')}</span>
          <span class="text-neutral-main2">{t('Your bet is successful')}</span>
          <span class="text-neutral-main2">{t('Loading new event...')}</span>
        </div>
      );
    case BetslipStatus.EMPTY:
      return 'Bets is Empty \n Place Your Bet';
    case BetslipStatus.ERROR:
      return (
        // 通用 error
        // <div class="flex flex-col items-center">
        //   <span class="text-neutral-main1 text-sm font-semibold">Bet Not Placed</span>
        //   <span class="text-neutral-main2">
        //     We couldn’t place your bet. Please try <br /> again or contact player support.
        //   </span>
        // </div>

        // 10013
        <div class="flex flex-col items-center">
          <span class="text-neutral-main1 text-sm font-semibold">{t('Bet Not Placed')}</span>
          <span class="text-neutral-main2">
            {t('Your bet was declined due to')} <br /> {t('odds changes. Accept the new odds?')}
          </span>
          <span>{t('Change Odds Settings')}</span>
        </div>
      );
    case BetslipStatus.SERVER_ERROR:
      return (
        <div class="flex flex-col items-center">
          <span class="text-neutral-main1 text-sm font-semibold">{t('Server Error')}</span>
          <span class="text-neutral-main2">{t('Server Error, Please try later.')}</span>
        </div>
      );
    case BetslipStatus.MBS_REPORT_AILED:
      return (
        <div class="flex flex-col items-center">
          <span class="text-neutral-main1 text-sm font-semibold">{t('MBS上报失败')}</span>
          <span class="text-neutral-main2">{t('Odds must be valid.')}</span>
        </div>
      );
    case BetslipStatus.NETWORK_ERROR:
      return (
        <div class="flex flex-col items-center">
          <span class="text-neutral-main1 text-sm font-semibold">{t('Network Error')}</span>
          <span class="text-neutral-main2">
            {t('Please check your internet')} <br /> {t('connection, and try again.')}
          </span>
        </div>
      );
    case BetslipStatus.TIMEOUT:
      return (
        <div class="flex flex-col items-center">
          <span class="text-neutral-main1 text-sm font-semibold">{t('Still Processing Your Bet')}</span>
          <span class="text-neutral-main2">
            {t('We’re finalizing your bet and will')} <br />
            {t('notify you once it’s done.')}
          </span>
        </div>
      );
    case BetslipStatus.BET_AMOUNT_TOO_SMALL:
      return (
        <div class="flex flex-col items-center">
          <span class="text-neutral-main1 text-sm font-semibold">{t('Minimum Bet Required')}</span>
          <span class="text-neutral-main2">
            {t('Your stake is below the minimum of')} {999}, <br />
            {t('Apply the minimum to proceed')}
          </span>
        </div>
      );
    case BetslipStatus.BET_AMOUNT_TOO_LARGE:
      return (
        <div class="flex flex-col items-center">
          <span class="text-neutral-main1 text-sm font-semibold">{t('Maximum Bet Reached')}</span>
          <span class="text-neutral-main2">
            {t('Stake exceeds')} {999}. <br />
            {t('Accept the maximum amount to proceed?')}
          </span>
        </div>
      );
    case BetslipStatus.SINGLE_ODDS_CHANGE:
      return 'Your bets have been partially placed! \n Change Odds Settings';
    case BetslipStatus.SINGLE_PARTIALLY_PLACED:
      return (
        // 10009 10010
        <div class="flex flex-col items-center">
          <span class="text-neutral-main1 text-sm font-semibold">{t('Partial Bet Accepted')}</span>
          <span class="text-neutral-main2">
            {t('Some selections couldn’t be')} <br />
            {t('submitted. Please try again.')}
          </span>
        </div>

        // 10012
        // <div class="flex flex-col items-center">
        //   <span class="text-neutral-main1 text-sm font-semibold">Partial Bet Accepted</span>
        //   <span class="text-neutral-main2">
        //     Your bet was declined due to <br />
        //     odds changes. Accept the new odds?
        //   </span>
        // </div>
      );
    case BetslipStatus.SINGLE_ODDS_PARTIALLY_PLACED:
      return (
        <div class="flex flex-col items-center">
          <span class="text-neutral-main1 text-sm font-semibold">{t('Partial Bet Accepted')}</span>
          <span class="text-neutral-main2">
            {t('Your bet was declined due to')} <br />
            {t('odds changes. Accept the new odds?')}
          </span>
        </div>
      );
    case BetslipStatus.SINGLE_ODDS_ALL_FAILED:
      return (
        <div class="flex flex-col items-center">
          <span class="text-neutral-main1 text-sm font-semibold">{t('Bet Not Placed')}</span>
          <span class="text-neutral-main2">
            {t('Your bet was declined due to')} <br />
            {t('odds changes. Accept the new odds?')}
          </span>
        </div>
      );
    case BetslipStatus.ALL_BET_FAILED:
      return (
        <div class="flex flex-col items-center">
          <span class="text-neutral-main1 text-sm font-semibold">{t('Bet Not Placed')}</span>
          <span class="text-neutral-main2">
            {t('Some selections couldn’t be')} <br />
            {t('submitted. Please try again.')}
          </span>
        </div>
      );
    case BetslipStatus.COMBO_ODDS_CHANGE:
      return 'Your bets was declined \n due to the odds change.\n Change Odds Settings';
    case BetslipStatus.COMBO_BALANCE_INSUFFICIENT:
      return '7.5$ Bet limit exceeded';
    case BetslipStatus.COMBO_MARKET_UNAVAILABLE:
      return 'Market unavailable anymore';
    default:
      return 'defalut';
  }
};

const getDefaultButtonByStatus = (props: any): any => {
  switch (props.status) {
    case BetslipStatus.LOADING:
      return null;
    case BetslipStatus.SUCCESS:
      return null;
    case BetslipStatus.ERROR:
      // 通用错误时的按钮
      if (!props.errorCode) {
        return (
          <Button onClick={props.onClose} type="brand" class="w-full my-4 h-11 font-semibold">
            {t('OK')}
          </Button>
        );
      }
      return (
        // 10013 全部下注失败（赔率变化）
        <div>
          <Button onClick={props.onClose} type="brand" class="w-full my-4 h-11 font-semibold">
            {t('Confirm')}
          </Button>
          <div class="center">
            <IconCheckWarning class="text-function-highlight size-[0.9rem]" />
            <span class="text-function-highlight text-xs font-semibold leading-[0.9rem] gap-2">
              {t('By clicking Confirm, you accept all odds change')}
            </span>
          </div>
        </div>
      );
    case BetslipStatus.NETWORK_ERROR:
      return (
        <Button onClick={props.onClose} type="brand" class="w-full my-4 h-11 font-semibold">
          {t('OK')}
        </Button>
      );
    case BetslipStatus.MBS_REPORT_AILED:
      return (
        <Button onClick={props.onClose} type="brand" class="w-full my-4 h-11 font-semibold">
          {t('Close')}
        </Button>
      );
    case BetslipStatus.SERVER_ERROR:
      return (
        <Button onClick={props.onClose} type="brand" class="w-full my-4 h-11 font-semibold">
          {t('Close')}
        </Button>
      );
    case BetslipStatus.TIMEOUT:
      return (
        <Button onClick={props.onClose} type="brand" class="w-full my-4 h-11 font-semibold">
          {t('OK')}
        </Button>
      );
    case BetslipStatus.BET_AMOUNT_TOO_SMALL:
      return (
        <div class="center">
          <Button onClick={props.onClose} type="brand" class="w-full my-4 h-11 font-semibold">
            {t('Close')}
          </Button>
        </div>
      );
    case BetslipStatus.BET_AMOUNT_TOO_LARGE:
      return (
        <div class="center">
          <Button
            onClick={props.onClose}
            type="brand"
            class="w-full my-4 h-11 bg-layer6 mr-2 text-neutral-main1 font-semibold"
          >
            {t('Cancel')}
          </Button>
          <Button onClick={props.onConfirm} type="brand" class="w-full my-4 h-11 font-semibold">
            {t('Confirm')}
          </Button>
        </div>
      );
    case BetslipStatus.SINGLE_ODDS_CHANGE:
      return (
        <Button onClick={props.onClose} type="brand" class="w-full my-4 h-11 font-semibold">
          {t('OK')}
        </Button>
      );
    case BetslipStatus.SINGLE_PARTIALLY_PLACED:
      return (
        // 10009 10010
        <Button onClick={props.onClose} type="brand" class="w-full my-4 h-11 font-semibold">
          {t('OK')}
        </Button>
      );
    case BetslipStatus.SINGLE_ODDS_PARTIALLY_PLACED: //10012 赔率变化 部分成功
      return (
        <div>
          <Button onClick={props.onConfirm} type="brand" class="w-full my-4 h-11 font-semibold">
            {t('Confirm')}
          </Button>
          <div class="center">
            <IconCheckWarning class="text-function-highlight size-[0.9rem]" />
            <span class="text-function-highlight text-xs font-semibold leading-[0.9rem] gap-2">
              {t('By clicking Confirm, you accept all odds change')}
            </span>
          </div>
        </div>
      );
    case BetslipStatus.SINGLE_ODDS_ALL_FAILED: //10013 赔率变化 所有失败
      return (
        <div>
          <Button onClick={props.onConfirm} type="brand" class="w-full my-4 h-11 font-semibold">
            {t('Confirm')}
          </Button>
          <div class="center">
            <IconCheckWarning class="text-function-highlight size-[0.9rem]" />
            <span class="text-function-highlight text-xs font-semibold leading-[0.9rem] gap-2">
              {t('By clicking Confirm, you accept all odds change')}
            </span>
          </div>
        </div>
      );
    case BetslipStatus.ALL_BET_FAILED:
      return (
        <Button onClick={props.onClose} type="brand" class="w-full my-4 h-11 font-semibold">
          {t('OK')}
        </Button>
      );
    case BetslipStatus.COMBO_ODDS_CHANGE:
      return (
        <Button onClick={props.onClose} type="brand" class="w-full my-4 h-11 font-semibold">
          {t('OK')}
        </Button>
      );
    case BetslipStatus.COMBO_BALANCE_INSUFFICIENT:
      return (
        <Button onClick={props.onClose} type="brand" class="w-full my-4 h-11 font-semibold">
          {t('OK')}
        </Button>
      );
    case BetslipStatus.COMBO_MARKET_UNAVAILABLE:
      return (
        <Button onClick={props.onClose} type="brand" class="w-full my-4 h-11 font-semibold">
          {t('OK')}
        </Button>
      );
    default:
      return t('defalut btn');
  }
};
